import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MessageCircle, CheckCircle } from 'lucide-react';

const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20visiting%20Sri%20Aurobindo%20Montessori%20School';

const growthCards = [
  {
    label: 'We grow deeper',
    body: 'We began with the Primary years, then trained in the Elementary method so the journey wouldn\'t end at six — and we\'re now extending toward Class 10. More years with each child, not more children each year.',
  },
  {
    label: 'We grow through people',
    body: 'Dozens of teachers who trained and grew here have gone on to open Montessori schools of their own, and we ask nothing in return. Quietly spreading this way of learning across the city has always been the point.',
  },
];

export default function OurPromise() {
  return (
    <div className="pt-16">
      <Helmet>
        <title>Our Promise: Why We've Stayed Small | Sri Aurobindo Montessori, Secunderabad</title>
        <meta name="description" content="For 25 years we've chosen fewer children and deeper attention over expansion — two AMI-trained guides per environment, no advertising, no compromise. Here's the choice we made, and why." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Our Promise</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="promise-h1"
          >
            We could have grown bigger.<br className="hidden sm:block" /> We chose to grow better.
          </h1>
        </div>
      </section>

      {/* ── OPENING CONTEXT ── */}
      <section className="py-20 bg-white" data-testid="opening-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-5 text-slate-600 leading-relaxed text-base">
            <p>
              You may have noticed something as you read our story. Twenty-five years, and more than 1200 children. If you've done the quiet arithmetic, that's a small number — around forty-odd children a year. A large school a few streets away might welcome that many into a single Class 1, across three or four sections.
            </p>
            <p>
              We know how that can look on paper. So let us tell you what that number really is.
            </p>
          </div>

          {/* Central quote */}
          <div className="mt-12 text-center">
            <p
              className="text-3xl sm:text-4xl font-black text-[#1E3A8A] leading-snug"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              It isn't how small we are.<br className="hidden sm:block" /> It's how carefully we've grown.
            </p>
            <p className="mt-4 text-slate-400 text-base font-semibold italic">
              We never counted enrolments. We counted childhoods.
            </p>
          </div>
        </div>
      </section>

      {/* ── CANNOT BE MASS-PRODUCED ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="mass-produced-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">The Honest Arithmetic</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Montessori cannot be mass-produced
          </h2>
          <div className="space-y-5 text-slate-600 leading-relaxed text-base">
            <p>
              This isn't modesty — it's a different kind of arithmetic. In an authentic Montessori environment, a guide works one-to-one with each child until six, and in small groups of four or five after. Mixed ages learn from one another; the guide observes each child closely, day after day.
            </p>
            <p>
              Put two hundred children through that promise and it quietly stops being true.
            </p>
            <p>
              To grow large, a school has to stop teaching this way — and we were never willing to.
            </p>
          </div>

          {/* Stat comparison */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="bg-white rounded-3xl px-7 py-7 shadow-[0_4px_20px_rgba(59,130,246,0.07)] border-t-4 border-[#1E3A8A]" data-testid="stat-our-school">
              <p className="text-4xl font-black text-[#1E3A8A] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>2</p>
              <p className="text-sm font-bold text-[#0F172A] leading-snug mb-1">AMI-trained guides in every environment</p>
              <p className="text-slate-400 text-xs leading-relaxed">Our standard — the most expensive way to run a school.</p>
            </div>
            <div className="bg-white rounded-3xl px-7 py-7 shadow-[0_4px_20px_rgba(59,130,246,0.07)] border-t-4 border-slate-200" data-testid="stat-global-norm">
              <p className="text-4xl font-black text-slate-300 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>1 : 40</p>
              <p className="text-sm font-bold text-slate-500 leading-snug mb-1">Global Montessori norm</p>
              <p className="text-slate-400 text-xs leading-relaxed">One trained adult permitted for forty-odd children.</p>
            </div>
          </div>
          <p className="mt-6 text-slate-500 text-sm leading-relaxed italic">
            More guides, fewer children: the most expensive way to run a school, and the only way we know to keep the promise.
          </p>
        </div>
      </section>

      {/* ── THE CHOICE ── */}
      <section className="py-20 bg-white" data-testid="choice-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Every Year, the Same Decision</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            The choice, made every single year
          </h2>
          <div className="space-y-5 text-slate-600 leading-relaxed text-base">
            <p>
              More families come to us than we can take — for every twenty or thirty who visit, we can offer a place to one. Every one of those years, there was an easier road: another section, another branch, more fees, faster growth. We didn't take it.
            </p>
            <p>
              We put what we had back into the things a parent can't see on a tour — sending our guides for further AMI training, and holding on to the very best of them at a time when trained Montessori teachers are courted by schools abroad.
            </p>
          </div>
          <div className="mt-10 bg-[#EFF6FF] rounded-3xl px-8 py-7 border-l-4 border-[#1E3A8A]">
            <p className="text-[#1E3A8A] font-black text-lg sm:text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
              We chose to grow in value for the child, not in numbers for ourselves.
            </p>
          </div>
        </div>
      </section>

      {/* ── TWO KINDS OF GROWTH ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="growth-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">What We Do Believe In</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-4 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Two kinds of growth we do believe in
          </h2>
          <p className="text-slate-600 text-base mb-10 leading-relaxed">
            We're not against growing — only against growing in the one direction that would cost children their childhoods.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {growthCards.map((card, i) => (
              <div
                key={i}
                data-testid={`growth-card-${i}`}
                className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(59,130,246,0.07)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-8 h-8 rounded-full bg-[#DBEAFE] flex items-center justify-center mb-5">
                  <CheckCircle size={16} className="text-[#1E3A8A]" />
                </div>
                <h3
                  className="text-lg font-extrabold text-[#1E3A8A] mb-3"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {card.label}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-slate-500 text-sm leading-relaxed italic">
            And none of this is a criticism of bigger schools. Wonderful children thrive in them every day. Different schools simply make different promises. This is the one we made — and have kept.
          </p>
        </div>
      </section>

      {/* ── WHAT IT MEANS FOR YOUR CHILD ── */}
      <section className="py-20 bg-white" data-testid="for-child-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">In Practice</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            What staying small means for your child
          </h2>
          <p className="text-slate-600 leading-relaxed text-base mb-8">
            Strip away the philosophy, and here is what it comes down to, for your child in particular:
          </p>
          <div className="bg-[#EFF6FF] rounded-3xl px-8 py-8 border-l-4 border-[#1E3A8A]">
            <p
              className="text-[#1E3A8A] font-black text-xl sm:text-2xl leading-snug"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              Here, your child is known.
            </p>
            <p className="text-slate-600 text-base leading-relaxed mt-4">
              Not a face in a hall of two hundred, but a person a guide watches over, understands, and gently helps forward — observed closely, by name, every single day.
            </p>
            <p className="text-slate-500 text-sm leading-relaxed mt-4 italic">
              That kind of attention has a price. We have always chosen to pay it, rather than pass the cost on to the child.
            </p>
          </div>
        </div>
      </section>

      {/* ── CLOSING PROMISE ── */}
      <section className="py-24 bg-[#1E3A8A]" data-testid="closing-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-6">A Promise Made to Children</p>
          <p
            className="text-3xl sm:text-4xl font-black text-white leading-snug mb-6"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            A school, we believe, is not a business that happens to teach children.
          </p>
          <p className="text-blue-200 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
            It is a promise made to children that happens to need a building.
          </p>
          <p className="text-blue-300 text-base leading-relaxed max-w-2xl mx-auto mb-12">
            For twenty-five years, keeping that promise has mattered to us more than outgrowing it. If that's the kind of place you've been hoping to find, we'd love to show it to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="promise-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Come and See <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="promise-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
